import { useCallback, useMemo, useRef } from 'react';
import { useAddMessage, useMessageQueueActions } from '@react-md/alert';
import { useAppSelector } from '@app/hooks';
import { selectAppCompanyName } from '@modules/app';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { TwoFactorResponse, useObtainAuthTokenCreateMutation, usePostV22FaVerifyMutation } from '@services/df/auth';
// import { createToast as toast } from '@shared/components/toast';
import { isFetchBaseQueryError } from '@utils/http';
import { SignInForm, TwoFactorForm } from './types';

const hasTwoFactor = (error: FetchBaseQueryError) => error?.status === 428;

const getTwoFactorResponse = (error: unknown) => {
    if (isFetchBaseQueryError(error) && hasTwoFactor(error)) {
        const response: TwoFactorResponse = error?.data; // {"next_action":"verify_2fa","two_fa_type":"email"}
        const shouldVerify = response.next_action === 'verify_2fa';
        return {
            shouldVerify,
            twoFactorType: response.two_fa_type,
            isTwoFactorStep: shouldVerify,
        };
    }
};

export const useAuthSignIn = () => {
    const addMessage = useAddMessage();

    const signInForm = useRef<SignInForm>();
    const companySlug = useRef<string>();

    const [_signInCompany, { isLoading: isTokenLoading, error }] = useObtainAuthTokenCreateMutation();
    const [_verify, { isLoading: isVerifying, reset: resetCode }] = usePostV22FaVerifyMutation();

    const { isTwoFactorStep, shouldVerify, twoFactorType } = getTwoFactorResponse(error) || {};

    const verify = useCallback(
        async (twoFactorForm: TwoFactorForm) => {
            const credentials = signInForm.current;

            if (!companySlug.current || !credentials) return;

            try {
                await _verify({
                    twoFactorAuthVerifyRequestRequest: {
                        company_name: companySlug.current,
                        ...credentials,
                        ...twoFactorForm,
                    },
                }).unwrap();
            } catch (error) {
                // if (isFetchBaseQueryError(error)) {
                //     toast({
                //         status: 'error',
                //         title: error.message,
                //         isClosable: true,
                //     });
                // }

                throw error;
            }
        },
        [_verify],
    );

    const createSignInCompany = useCallback(
        (company_name: string) => async (_form?: SignInForm) => {
            companySlug.current = company_name;

            if (_form) {
                signInForm.current = _form;
            }

            const credentials = signInForm.current;

            if (!credentials) return;

            try {
                await _signInCompany({
                    obtainTokenRequestRequest: {
                        company_name,
                        ...credentials,
                    },
                }).unwrap();
            } catch (error) {
                // if (isFetchBaseQueryError(error) && !hasTwoFactor(error)) {
                //     toast({
                //         status: 'error',
                //         title: error.message,
                //         isClosable: true,
                //     });
                // }
            }
        },
        [_signInCompany],
    );

    return {
        isTokenLoading,
        createSignInCompany,

        shouldVerify,
        twoFactorType,
        isTwoFactorStep,
        isVerifying,
        verify,
        resetCode,
    };
};

export const useCompanySignInSubmit = () => {
    const company = useAppSelector(selectAppCompanyName);
    const { createSignInCompany, ...rest } = useAuthSignIn();
    const signInSubmit = useMemo(() => createSignInCompany(company), [createSignInCompany, company]);

    return { signInSubmit, ...rest };
};
